/**
 * 运营-商户管理（入驻审核 + 启停）
 * F1 入驻流程的审核环节
 */

import { useState } from 'react'
import { Card, Table, Button, Badge, useApi, time } from '../../components/ui'
import { api } from '../../api/client'
import { HelpBubble } from '../../components/HelpBubble'

interface Merchant {
  merchant_id: string
  type: string
  company_name: string
  credit_code: string
  legal_person: string
  tax_no: string
  register_region: string
  status: string
  reject_reason: string | null
  created_at: string
}

export function AdminMerchants() {
  const [status, setStatus] = useState('PENDING')
  const list = useApi<Merchant[]>(async () => api.get(`/admin/merchants?status=${status}`), [status])

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>商户管理</h2>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['PENDING', 'ACTIVE', 'SUSPENDED', 'REJECTED'].map((s) => (
          <Button key={s} variant={status === s ? 'primary' : 'default'} size="sm" onClick={() => setStatus(s)}>
            {s === 'PENDING' ? '待审核' : s === 'ACTIVE' ? '已激活' : s === 'SUSPENDED' ? '已停用' : '已驳回'}
          </Button>
        ))}
      </div>

      <Card title={`商户列表（${list.data?.length ?? 0}）`}>
        <Table<Merchant>
          rows={list.data ?? []}
          empty={list.loading ? '加载中...' : '该状态暂无商户（新商户在 /apply 提交申请后会出现在「待审核」）'}
          columns={[
            { key: 'company_name', title: '企业名称' },
            { key: 'type', title: '类型', render: (r) => (r.type === 'FACTORY' ? '工厂' : '商家') },
            { key: 'credit_code', title: '信用代码' },
            { key: 'legal_person', title: '法人' },
            { key: 'created_at', title: '申请时间', render: (r) => time(r.created_at) },
            { key: 'status', title: '状态', render: (r) => <Badge status={r.status} /> },
            {
              key: 'action',
              title: '操作',
              render: (r) => <Actions merchant={r} onDone={() => list.refresh()} />,
            },
          ]}
        />
      </Card>
      <HelpBubble pageKey="admin-merchants" title="商户管理">
        <p>商户入驻流程：商家/工厂提交申请（/apply 页）→ 运营在此<b>审核通过/驳回</b> → 通过后自动开立银行虚拟子户。</p>
        <p>可按状态筛选（待审核/已激活/已停用）。点「通过」激活商户，点「驳回」需填原因。</p>
      </HelpBubble>
    </div>
  )
}

function Actions({ merchant, onDone }: { merchant: Merchant; onDone: () => void }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function approve() {
    setBusy(true)
    setErr('')
    try {
      await api.post(`/admin/merchants/${merchant.merchant_id}/approve`)
      onDone()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  async function reject() {
    const reason = prompt('驳回原因：')
    if (!reason) return
    setBusy(true)
    setErr('')
    try {
      await api.post(`/admin/merchants/${merchant.merchant_id}/reject`, { reason })
      onDone()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  async function suspend() {
    if (!confirm('确定停用？')) return
    setBusy(true)
    try {
      await api.post(`/admin/merchants/${merchant.merchant_id}/suspend`)
      onDone()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  if (merchant.status === 'PENDING') {
    return (
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <Button size="sm" onClick={approve} disabled={busy}>
          通过
        </Button>
        <Button size="sm" variant="danger" onClick={reject} disabled={busy}>
          驳回
        </Button>
        {err && <span style={{ color: '#dc2626', fontSize: 11 }}>{err}</span>}
      </div>
    )
  }
  if (merchant.status === 'ACTIVE') {
    return (
      <Button size="sm" variant="default" onClick={suspend} disabled={busy}>
        停用
      </Button>
    )
  }
  return <span style={{ color: '#888' }}>-</span>
}
